/*
 © Copyright 2026 Daikin Industries, Ltd. All rights reserved.

 This software is the proprietary information of Daikin Industries, Ltd.
 By using this software, you agree to the following terms:

 1. You are granted a revocable non-exclusive, non-transferable license to use this software.
 2. You agree not to modify, distribute, or create derivative works based on this software.
 3. Unauthorized copying, distribution, or use of any portion of this software is strictly prohibited.
 4. This software is protected by copyright laws and international treaties.
 5. Any violation of these terms will result in the termination of your license and may result in legal action.
 6. This is Daikin's proprietary software and may not be used in an unauthorized manner or without express authorization from Daikin.
*/

/**
 * Swagger State Management Models
 *
 * This file contains interfaces and types for managing Swagger UI state
 * including authentication, OpenAPI specs, and pending requests.
 */

/**
 * OpenAPI Server definition with subscription key
 */
export interface OpenAPIServer {
  url: string;
  description?: string;
  'x-subscriptionKey'?: string;
  variables?: Record<string, any>;
}

/**
 * OpenAPI Specification structure (subset of relevant fields)
 */
export interface OpenAPISpec {
  openapi?: string;
  swagger?: string;
  info?: {
    title: string;
    version: string;
    description?: string;
  };
  servers?: OpenAPIServer[];
  paths?: Record<string, any>;
  components?: {
    securitySchemes?: Record<string, any>;
    schemas?: Record<string, any>;
  };
  security?: Array<Record<string, string[]>>;
}

/**
 * Swagger request information
 */
export interface SwaggerRequest {
  url: string;
  method: string;
  headers: Record<string, string>;
  body?: any;
  timestamp?: number;
}

/**
 * Pending request tracking (combines display and proxy URLs)
 */
export interface PendingRequest {
  originalRequest: SwaggerRequest;
  displayRequest: SwaggerRequest;
  proxyUrl: string;
  timestamp: number;
}

/**
 * Main Swagger state interface
 */
export interface SwaggerState {
  /** Current authentication token */
  authToken: string | null;

  /** Current OpenAPI specification */
  swaggerSpec: OpenAPISpec | null;

  /** Map of pending requests by request URL */
  pendingRequests: Map<string, PendingRequest>;

  /** Set of currently active operation IDs */
  activeOperations: Set<string>;

  /** Last known original request (for backward compatibility) */
  lastOriginalRequest: SwaggerRequest | null;

  /** Last known proxy URL (for backward compatibility) */
  lastProxyUrl: string | null;

  /** Last known original URL (for backward compatibility) */
  lastOriginalUrl: string | null;
}

/**
 * Initial/default state
 */
export const DEFAULT_SWAGGER_STATE: SwaggerState = {
  authToken: null,
  swaggerSpec: null,
  pendingRequests: new Map(),
  activeOperations: new Set(),
  lastOriginalRequest: null,
  lastProxyUrl: null,
  lastOriginalUrl: null
};
